/**
 * Single/Dual/Trio Bundle Selector
 * Handles model selection, bundle quantity selection, variant radio buttons, and cart integration.
 */

class SingleDualBundleSelector {
  constructor(container) {
    this.container = container;
    this.config = this.#loadConfig();
    this.isAddingToCart = false;
    this.isUpdating = false;
    this.intelligemsRefreshTimer = null;
    this.intelligemsRefreshAttempts = 0;
    this.intelligemsRefreshVariantId = '';
    this.isIntelligemsRefreshTicking = false;
    this.instanceId = this.config?.instanceId || this.container.id || 'single-dual-bundle';
    this.isScopedMode = this.config?.selectionMode === 'scoped';
    this.scopedState = null;

    this.modelRadios = this.container.querySelectorAll('[data-model-radio]');
    this.optionRadios = this.container.querySelectorAll('[data-option-radio]');
    this.optionCards = this.container.querySelectorAll('[data-option-card]');
    this.typeOptionsContainer = this.container.querySelector('[data-type-options]');
    this.expandableSlotSingle = this.container.querySelector('[data-bundle-expandable-slot="single"]');
    this.optionItemDual = this.container.querySelector('[data-option-item="dual"]');
    this.optionItemTrio = this.container.querySelector('[data-option-item="trio"]');
    this.expandableSlotDual = this.container.querySelector('[data-bundle-expandable-slot="dual"]');
    this.expandableSlotTrio = this.container.querySelector('[data-bundle-expandable-slot="trio"]');
    this.expandableStorage = this.container.querySelector('[data-bundle-expandable-storage]');
    this.bundleExpandable = this.container.querySelector('[data-bundle-expandable]');
    this.notificationBox = this.container.querySelector('[data-bundle-notification-box]');
    this.bundleInfoText = this.container.querySelector('[data-bundle-info-text]');
    this.bundleGiftFooter = this.container.querySelector('[data-bundle-gift-footer]');
    this.bundleGiftFooterText = this.container.querySelector('[data-bundle-gift-footer-text]');
    this.variantGroup3 = this.container.querySelector('[data-variant-group="3"]');
    this.summaryText = this.container.querySelector('[data-selection-summary]');
    this.optionInput = this.container.querySelector('[data-single-dual-option]');
    this.bundleVariant1Input = this.container.querySelector('[data-bundle-variant="1"]');
    this.bundleVariant2Input = this.container.querySelector('[data-bundle-variant="2"]');
    this.bundleVariant3Input = this.container.querySelector('[data-bundle-variant="3"]');
    this.scopedGlobalSection = this.container.querySelector('[data-scoped-global-section]');
    this.scopedGlobalLabel = this.container.querySelector('[data-scoped-global-label]');
    this.scopedGlobalOptions = this.container.querySelector('[data-scoped-global-options]');
    this.scopedVariantGroups = this.container.querySelector('[data-scoped-variant-groups]');
    this.variantOptionsContainers = {
      2: this.container.querySelector('[data-variant-options="2"]'),
      3: this.container.querySelector('[data-variant-options="3"]'),
      4: this.container.querySelector('[data-variant-options="4"]')
    };

    this.singlePriceElement = this.container.querySelector('[data-single-price]');
    this.singleComparePriceElement = this.container.querySelector('[data-single-compare-price]');
    this.singleSavingsAmountElement = this.container.querySelector('[data-single-savings-amount]');
    this.singleSavingsPercentElement = this.container.querySelector('[data-single-savings-percent]');
    this.duoPriceElement = this.container.querySelector('[data-duo-price]');
    this.duoComparePriceElement = this.container.querySelector('[data-duo-compare-price]');
    this.trioPriceElement = this.container.querySelector('[data-trio-price]');
    this.trioComparePriceElement = this.container.querySelector('[data-trio-compare-price]');
    this.savingsBadgeElements = {
      single: this.container.querySelector('[data-savings-badge="single"]'),
      dual: this.container.querySelector('[data-savings-badge="dual"]'),
      trio: this.container.querySelector('[data-savings-badge="trio"]')
    };
    this.savingsValueElements = {
      single: this.container.querySelector('[data-savings-value="single"]'),
      dual: this.container.querySelector('[data-savings-value="dual"]'),
      trio: this.container.querySelector('[data-savings-value="trio"]')
    };

    this.models = this.#buildModels();
    this.selectedModelKey = this.#getInitialModelKey();

    if (!this.config || !this.models.primary) {
      return;
    }

    this.#init();
  }

  #init() {
    this.#disableThemeVariantPicker();
    this.#underlineGratisInGiftPills();
    this.#attachEventListeners();
    this.#setModelRadioState();
    this.#restoreOptionFromUrl();
    this.#renderActiveModel();
    this.#handleOptionChange();
    this.#registerIntelligemsLifecycleListeners();
    this.#scheduleIntelligemsRefresh();
    this.#overrideAddToCart();
  }

  #loadConfig() {
    const configElement = this.container.querySelector('[data-bundle-config]');
    if (!configElement) return null;

    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('Single/Dual Bundle: Failed to parse config', error);
      return null;
    }
  }

  #buildModels() {
    if (!this.config) return {};

    const sourceModels = this.config.models || {
      primary: {
        key: 'primary',
        productId: this.config.currentProductId,
        variants: this.config.variants || [],
        optionIndex: this.config.optionIndex || 0,
        optionName: this.config.optionName || '',
        selectedVariantId: this.config.selectedVariantId,
        singlePrice: this.config.singlePrice,
        singleComparePrice: this.config.singleComparePrice,
        duoPrice: this.config.duoPrice,
        duoComparePrice: this.config.duoComparePrice,
        trioPrice: this.config.trioPrice,
        trioComparePrice: this.config.trioComparePrice
      }
    };

    const normalized = {};

    Object.entries(sourceModels).forEach(([key, model]) => {
      if (!model || !Array.isArray(model.variants) || model.variants.length === 0) return;

      const prepared = {
        key,
        productId: model.productId || null,
        handle: model.handle || '',
        url: model.url || '',
        title: model.title || '',
        subtitle: model.subtitle || '',
        image: model.image || '',
        optionNames: Array.isArray(model.optionNames) ? model.optionNames : [],
        optionName: model.optionName || '',
        optionIndex: Number.isInteger(model.optionIndex) ? model.optionIndex : 0,
        selectedVariantId: model.selectedVariantId ? String(model.selectedVariantId) : null,
        overrideImages: Array.isArray(model.overrideImages) ? model.overrideImages : [],
        variantDescriptions: Array.isArray(model.variantDescriptions) ? model.variantDescriptions : [],
        useEbikeStyleLabels: key === 'primary' || Boolean(model.useEbikeStyleLabels),
        variants: model.variants,
        singlePrice: Number(model.singlePrice || 0),
        singleComparePrice: Number(model.singleComparePrice || 0),
        duoPrice: Number(model.duoPrice || 0),
        duoComparePrice: Number(model.duoComparePrice || 0),
        trioPrice: Number(model.trioPrice || 0),
        trioComparePrice: Number(model.trioComparePrice || 0),
        enablePriceTestingMode: Boolean(model.enablePriceTestingMode),
        testingDuoDiscount: Number(model.testingDuoDiscount || 0),
        testingTrioDiscount: Number(model.testingTrioDiscount || 0),
        singleDiscountBadge: Number(model.singleDiscountBadge || 0),
        duoDiscountBadge: Number(model.duoDiscountBadge || 0),
        trioDiscountBadge: Number(model.trioDiscountBadge || 0)
      };

      prepared.scopedGlobalOption = this.#buildScopedOptionDefinition(
        prepared,
        this.config.scopedGlobalOptionName,
        this.config.scopedGlobalOptionLabel
      );
      prepared.scopedItemOptions = (this.config.scopedItemOptions || [])
        .map((option) => this.#buildScopedOptionDefinition(prepared, option?.name, option?.label))
        .filter(Boolean);
      const configuredItemOptionCount = (this.config.scopedItemOptions || [])
        .filter((option) => String(option?.name || '').trim()).length;
      prepared.scopedConfigurationValid = !this.isScopedMode || Boolean(
        configuredItemOptionCount > 0
        && prepared.scopedItemOptions.length === configuredItemOptionCount
        && (!String(this.config.scopedGlobalOptionName || '').trim() || prepared.scopedGlobalOption)
      );

      prepared.selectorVariants = this.#buildSelectorVariants(prepared);
      prepared.defaultVariantId = this.#getDefaultVariantId(prepared);
      prepared.duoPricePerItem = Math.round(prepared.duoPrice / 2);
      prepared.duoComparePricePerItem = Math.round(prepared.duoComparePrice / 2);
      prepared.trioPricePerItem = Math.round(prepared.trioPrice / 3);
      prepared.trioComparePricePerItem = Math.round(prepared.trioComparePrice / 3);

      normalized[key] = prepared;
    });

    return normalized;
  }

  #buildScopedOptionDefinition(model, optionName, optionLabel) {
    const normalizedName = String(optionName || '').trim();
    if (!normalizedName) return null;

    const optionIndex = model.optionNames.findIndex((name) => String(name) === normalizedName);
    if (optionIndex < 0) return null;

    const values = [];
    model.variants.forEach((variant) => {
      const value = variant.options?.[optionIndex];
      if (value != null && value !== '' && !values.includes(String(value))) {
        values.push(String(value));
      }
    });

    return {
      name: normalizedName,
      label: String(optionLabel || normalizedName).trim() || normalizedName,
      index: optionIndex,
      values
    };
  }

  #buildSelectorVariants(model) {
    const seenTitles = new Set();
    const selectorVariants = [];

    model.variants.forEach((variant) => {
      const rawTitle = variant.options?.[model.optionIndex] ?? variant.title ?? '';
      if (!rawTitle || seenTitles.has(rawTitle)) return;

      seenTitles.add(rawTitle);

      selectorVariants.push({
        id: String(variant.id),
        rawTitle,
        displayTitle: this.#formatOptionDisplay(rawTitle, { useEbikeLabels: model.useEbikeStyleLabels }),
        description: (model.variantDescriptions[selectorVariants.length] || '').trim(),
        imageUrl: model.overrideImages[selectorVariants.length] || this.#getVariantImageUrl(variant),
        variant
      });
    });

    return selectorVariants;
  }

  #getDefaultVariantId(model) {
    const selectedVariantId = model.selectedVariantId && String(model.selectedVariantId);
    if (this.isScopedMode) {
      const selectedVariant = this.#findVariantById(model, selectedVariantId);
      if (selectedVariant?.available) return selectedVariantId;
      return String(model.variants.find((variant) => variant.available)?.id || model.variants[0]?.id || '') || null;
    }
    const selectedSelectorVariant = model.selectorVariants.find((variant) => variant.id === selectedVariantId);
    if (selectedSelectorVariant) return selectedSelectorVariant.id;
    return model.selectorVariants[0]?.id || null;
  }

  #getInitialModelKey() {
    const initialVariantId = this.#getInitialVariantId();

    if (initialVariantId) {
      const matchingModelKey = Object.keys(this.models).find((key) =>
        this.models[key]?.selectorVariants.some((variant) => variant.id === String(initialVariantId))
      );
      if (matchingModelKey) return matchingModelKey;
    }

    return this.models.secondary ? 'primary' : 'primary';
  }

  #getInitialVariantId() {
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    if (urlVariantId) return String(urlVariantId);

    const productForm = this.container.closest('.product-form');
    const form = this.container.closest('form')
      || productForm?.querySelector('form[is="product-form"]')
      || productForm?.querySelector('form');
    const formVariantInput = form?.querySelector('input[name="id"]');

    if (formVariantInput?.value) {
      return String(formVariantInput.value);
    }

    return this.models.primary?.defaultVariantId || null;
  }

  #disableThemeVariantPicker() {
    const productVariants = this.container.closest('.product-form')?.querySelector('product-variants');
    if (productVariants) {
      productVariants.style.display = 'none';
      const variantInputs = productVariants.querySelectorAll('input, select');
      for (const input of variantInputs) {
        input.disabled = true;
      }
    }

    const productForm = this.container.closest('.product-form');
    if (productForm) {
      const hiddenVariantInput = productForm.querySelector('input[name="id"]');
      if (hiddenVariantInput) {
        hiddenVariantInput.disabled = true;
      }
    }
  }

  #underlineGratisInGiftPills() {
    this.container.querySelectorAll('.single-dual-bundle__gift-pill').forEach((el) => {
      if (el.innerHTML && el.innerHTML.includes('Gratis')) {
        el.innerHTML = this.#underlineGratisMarkup(el.innerHTML);
      }
    });

    this.container.querySelectorAll('[data-gratis-copy]').forEach((el) => {
      const rawText = el.getAttribute('data-raw-text') || el.textContent || '';
      this.#renderGratisCopy(el, rawText);
    });
  }

  #formatGratisCopy(rawText) {
    return this.#escapeHtml(rawText).replace(/Gratis/g, '<span class="single-dual-bundle__gratis-underlined">Gratis</span>');
  }

  #underlineGratisMarkup(rawMarkup) {
    return String(rawMarkup ?? '').replace(/Gratis/g, '<span class="single-dual-bundle__gratis-underlined">Gratis</span>');
  }

  #renderGratisCopy(element, rawText) {
    if (!element) return;

    element.setAttribute('data-raw-text', rawText || '');
    element.innerHTML = this.#formatGratisCopy(rawText || '');
  }

  #attachEventListeners() {
    this.container.addEventListener('change', (event) => {
      const target = event.target;

      if (target.matches('[data-model-radio]')) {
        this.#handleModelChange(target.value);
        return;
      }

      if (target.matches('[data-option-radio]')) {
        this.#handleOptionChange();
        return;
      }

      if (target.matches('[data-scoped-global-input]')) {
        this.#handleScopedGlobalChange(target.value);
        return;
      }

      if (target.matches('[data-scoped-item-input]')) {
        this.#handleScopedItemChange(target);
        return;
      }

      if (target.matches('[data-type-radio="1"]')) {
        this.#syncFromMainVariant();
        return;
      }

      if (target.matches('[data-variant-radio]')) {
        this.#handleVariantRadioChange();
      }
    });

    this.container.addEventListener('click', (event) => {
      if (
        event.target.closest('.popover-text') ||
        event.target.closest('button[is="toggle-button"]')
      ) {
        return;
      }

      event.stopPropagation();
    }, true);

    this.container.addEventListener('keydown', (event) => {
      if ((event.key === 'Enter' || event.key === ' ') && event.target?.matches?.('[tabindex="0"]')) {
        event.preventDefault();
        event.target.click();
      }
    });

    window.addEventListener('resize', () => {
      if (this.isScopedMode) return;
      const model = this.#getActiveModel();
      if (!model) return;

      this.#applyResponsiveTypeOptionsLayout(model.selectorVariants.length || 1);
    });
  }

  #handleModelChange(modelKey) {
    if (!this.models[modelKey] || this.selectedModelKey === modelKey) return;

    const targetModel = this.models[modelKey];
    if (targetModel && Number(targetModel.productId) !== Number(this.config.currentProductId)) {
      this.#navigateToModelProduct(targetModel);
      return;
    }

    this.selectedModelKey = modelKey;
    this.#renderActiveModel({ resetSelections: true });
    this.#handleOptionChange();
  }

  #restoreOptionFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const bundleOption = params.get('bundle_option');
    if (!bundleOption || !['single', 'dual', 'trio'].includes(bundleOption)) return;

    const radio = this.container.querySelector(`[data-option-radio][value="${bundleOption}"]`);
    if (radio) {
      radio.checked = true;
    }
  }

  #navigateToModelProduct(targetModel) {
    const targetUrl = this.#getModelUrl(targetModel);
    if (!targetUrl) return;

    const url = new URL(targetUrl, window.location.origin);
    const option = this.#getSelectedOption();
    const currentRawTitle = this.#getCurrentSelectionRawTitleForNavigation();
    const matchingVariant = this.#findMatchingSelectorVariant(targetModel, currentRawTitle);
    const variantId = matchingVariant?.id || targetModel.defaultVariantId;

    if (variantId) {
      url.searchParams.set('variant', variantId);
    }

    if (option) {
      url.searchParams.set('bundle_option', option);
    }

    window.location.href = url.toString();
  }

  #getModelUrl(model) {
    if (model.url) return model.url;
    if (model.handle) {
      const root = window.Shopify?.routes?.root || '/';
      return `${root.replace(/\/$/, '')}/products/${model.handle}`;
    }

    return '';
  }

  #getCurrentSelectionTitleForNavigation() {
    if (this.isScopedMode) {
      const variantId = this.#getScopedVariantIds()[0];
      return this.#getScopedVariantDisplayTitle(this.#getActiveModel(), variantId);
    }

    const option = this.#getSelectedOption();
    if (option === 'single') {
      return this.container.querySelector('[data-type-radio="1"]:checked')?.getAttribute('data-variant-title') || '';
    }

    return this.#getVariantTitleFromRadio(2)
      || this.container.querySelector('[data-type-radio="1"]:checked')?.getAttribute('data-variant-title')
      || '';
  }

  #getCurrentSelectionRawTitleForNavigation() {
    if (this.isScopedMode) {
      const variantId = this.#getScopedVariantIds()[0];
      const model = this.#getActiveModel();
      const variant = this.#findVariantById(model, variantId);
      return variant?.options?.[model.optionIndex] || '';
    }

    const option = this.#getSelectedOption();
    if (option === 'single') {
      return this.container.querySelector('[data-type-radio="1"]:checked')?.getAttribute('data-variant-raw-title') || '';
    }

    return this.container.querySelector('[data-variant-radio="2"]:checked')?.getAttribute('data-variant-raw-title')
      || this.container.querySelector('[data-type-radio="1"]:checked')?.getAttribute('data-variant-raw-title')
      || '';
  }

  #findMatchingSelectorVariant(model, rawOrDisplayTitle) {
    const targetKey = this.#normalizeStyleKey(rawOrDisplayTitle);
    if (!targetKey || !model?.selectorVariants?.length) return null;

    return model.selectorVariants.find((variant) => this.#normalizeStyleKey(variant.rawTitle) === targetKey)
      || model.selectorVariants.find((variant) => this.#normalizeStyleKey(variant.displayTitle) === targetKey)
      || null;
  }

  #normalizeStyleKey(title) {
    let key = String(title || '').split(' - ')[0].trim().toLowerCase();
    if (key === 'mountainbike') key = 'mtb';
    if (key === 'trekking-e-bike') key = 'trekking';
    if (key === 'city-e-bike') key = 'city';
    if (key === 'e-mtb') key = 'mtb';
    return key;
  }

  #renderActiveModel({ resetSelections = false } = {}) {
    const model = this.#getActiveModel();
    if (!model) return;

    if (this.isScopedMode) {
      this.#initializeScopedState(model, resetSelections);
      this.#setModelRadioState();
      this.#renderScopedSelection(model);
      this.#updateVariantInputs();
      this.#updatePricing(model);
      this.#updateSummary();
      this.#updateBundleNotification();
      this.#updateImageGalleryForVariant();
      this.#updateAddToCartAvailability();
      return;
    }

    const selections = this.#resolveSelections(model, resetSelections);

    this.#setModelRadioState();
    this.#renderTypeOptions(model, selections.main);
    this.#renderVariantOptions(model, 2, selections.variant2);
    this.#renderVariantOptions(model, 3, selections.variant3);
    this.#renderVariantOptions(model, 4, selections.variant4);
    this.#updatePricing(model);
    this.#updateVariantInputs();
    this.#updateSummary();
    this.#updateImageGalleryForVariant();
  }

  #initializeScopedState(model, resetSelections = false) {
    const initialVariantId = resetSelections ? model.defaultVariantId : this.#getInitialVariantId();
    const initialVariant = this.#findVariantById(model, initialVariantId)
      || this.#findVariantById(model, model.defaultVariantId)
      || model.variants.find((variant) => variant.available)
      || model.variants[0];
    const initialGlobalValue = model.scopedGlobalOption
      ? String(initialVariant?.options?.[model.scopedGlobalOption.index] || model.scopedGlobalOption.values[0] || '')
      : '';

    if (!this.scopedState || resetSelections) {
      const itemState = this.#getScopedItemStateFromVariant(model, initialVariant);
      this.scopedState = {
        globalValue: initialGlobalValue,
        items: [0, 1, 2].map(() => ({ ...itemState }))
      };
    } else if (model.scopedGlobalOption && !model.scopedGlobalOption.values.includes(this.scopedState.globalValue)) {
      this.scopedState.globalValue = initialGlobalValue;
    }

    this.scopedState.items = [0, 1, 2].map((index) => {
      const itemState = this.scopedState.items[index] || this.#getScopedItemStateFromVariant(model, initialVariant);
      return this.#ensureScopedItemSelection(model, itemState);
    });
  }

  #getScopedItemStateFromVariant(model, variant) {
    const state = {};
    model.scopedItemOptions.forEach((option) => {
      state[option.index] = String(variant?.options?.[option.index] || option.values[0] || '');
    });
    return state;
  }

  #renderScopedSelection(model) {
    if (!this.scopedState) return;

    this.#renderScopedGlobalOptions(model);
    if (this.scopedVariantGroups) {
      this.scopedVariantGroups.innerHTML = this.#buildScopedItemGroupsMarkup(
        model,
        this.#getSelectedOption() === 'trio' ? 3 : this.#getSelectedOption() === 'dual' ? 2 : 1
      );
    }
  }

  #renderScopedGlobalOptions(model) {
    const option = model.scopedGlobalOption;
    if (!this.scopedGlobalSection || !this.scopedGlobalOptions) return;

    this.scopedGlobalSection.hidden = !option;
    if (!option) {
      this.scopedGlobalOptions.innerHTML = '';
      return;
    }

    if (this.scopedGlobalLabel) this.scopedGlobalLabel.textContent = option.label;
    this.scopedGlobalOptions.className = 'single-dual-bundle__type-options';
    this.scopedGlobalOptions.style.gridTemplateColumns = `repeat(${Math.min(option.values.length, 4)}, minmax(0, 1fr))`;
    this.scopedGlobalOptions.innerHTML = option.values.map((value, index) => {
      const representativeVariant = model.variants.find((variant) => String(variant.options?.[option.index]) === value && variant.available)
        || model.variants.find((variant) => String(variant.options?.[option.index]) === value);
      const isAvailable = model.variants.some((variant) => String(variant.options?.[option.index]) === value && variant.available);
      const checked = value === this.scopedState.globalValue ? 'checked' : '';
      const disabled = isAvailable ? '' : 'disabled';
      const id = `sd-scoped-global-${this.instanceId}-${index}`;
      const imageUrl = this.#getVariantImageUrl(representativeVariant);

      return `
        <div class="single-dual-bundle__type-option">
          <input type="radio" id="${id}" name="sd-scoped-global-${this.instanceId}" value="${this.#escapeHtml(value)}" class="single-dual-bundle__type-radio" data-scoped-global-input ${checked} ${disabled}>
          <label for="${id}" class="single-dual-bundle__type-label" tabindex="0">
            <div class="single-dual-bundle__type-image">
              <div class="single-dual-bundle__type-checkmark"><svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg></div>
              ${imageUrl ? `<img src="${this.#escapeHtml(imageUrl)}" alt="${this.#escapeHtml(value)}" width="200" height="200" loading="lazy">` : ''}
            </div>
            <div class="single-dual-bundle__type-info"><div class="single-dual-bundle__type-name">${this.#escapeHtml(value)}</div></div>
          </label>
        </div>
      `;
    }).join('');
  }

  #buildScopedItemGroupsMarkup(model, quantity) {
    const unitLabel = String(this.config?.itemUnitLabel || 'Artikel').trim() || 'Artikel';
    return Array.from({ length: quantity }, (_, itemIndex) => {
      const groupLabel = `${unitLabel} ${itemIndex + 1}`;
      const fields = model.scopedItemOptions
        .map((option) => this.#buildScopedOptionFieldMarkup(model, option, itemIndex))
        .join('');
      return `
        <div class="single-dual-bundle__variant-group">
          <label class="single-dual-bundle__variant-group-label">${this.#escapeHtml(groupLabel)}</label>
          ${fields}
        </div>
      `;
    }).join('');
  }

  #buildScopedOptionFieldMarkup(model, option, itemIndex) {
    const selectedValue = this.scopedState.items[itemIndex]?.[option.index] || '';
    const options = option.values.map((value, valueIndex) => {
      const id = `sd-scoped-${this.instanceId}-${itemIndex}-${option.index}-${valueIndex}`;
      const checked = value === selectedValue ? 'checked' : '';
      const disabled = this.#isScopedOptionValueAvailable(model, itemIndex, option, value) ? '' : 'disabled';
      const displayValue = this.#getScopedOptionValueDisplay(option, value);
      return `
        <div class="single-dual-bundle__variant-option">
          <input type="radio" id="${id}" name="sd-scoped-${this.instanceId}-${itemIndex}-${option.index}" value="${this.#escapeHtml(value)}" class="single-dual-bundle__variant-option-radio" data-scoped-item-input data-item-index="${itemIndex}" data-option-index="${option.index}" ${checked} ${disabled}>
          <label for="${id}" class="single-dual-bundle__variant-option-label" tabindex="0">
            <span class="single-dual-bundle__variant-option-dot"></span>
            <span class="single-dual-bundle__variant-option-name">${this.#escapeHtml(displayValue)}</span>
          </label>
        </div>
      `;
    }).join('');

    return `
      <div class="single-dual-bundle__scoped-field">
        <label class="single-dual-bundle__variant-group-label">${this.#escapeHtml(option.label)}</label>
        <div class="single-dual-bundle__variant-options">${options}</div>
      </div>
    `;
  }

  #getScopedOptionValueDisplay(option, value) {
    const rawValue = String(value || '').trim();
    if (!rawValue) return '';

    const normalizedOptionNames = [option?.label, option?.name]
      .map((name) => String(name || '').trim())
      .filter(Boolean);

    for (const optionName of normalizedOptionNames) {
      const escapedOptionName = optionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const prefixPattern = new RegExp(`^${escapedOptionName}\\s+`, 'i');
      if (prefixPattern.test(rawValue)) {
        return rawValue.replace(prefixPattern, '').trim();
      }
    }

    return rawValue;
  }

  #handleScopedGlobalChange(value) {
    const model = this.#getActiveModel();
    if (!model?.scopedGlobalOption || !model.scopedGlobalOption.values.includes(value)) return;
    this.scopedState.globalValue = value;
    this.scopedState.items = this.scopedState.items.map((itemState) => this.#ensureScopedItemSelection(model, itemState));
    this.#refreshScopedSelection(model);
  }

  #handleScopedItemChange(input) {
    const model = this.#getActiveModel();
    const itemIndex = Number(input.dataset.itemIndex);
    const optionIndex = Number(input.dataset.optionIndex);
    if (!model || !Number.isInteger(itemIndex) || !Number.isInteger(optionIndex)) return;

    const nextState = { ...(this.scopedState.items[itemIndex] || {}) };
    nextState[optionIndex] = input.value;
    this.scopedState.items[itemIndex] = this.#ensureScopedItemSelection(model, nextState);
    this.#refreshScopedSelection(model);
  }

  #refreshScopedSelection(model) {
    this.#renderScopedSelection(model);
    this.#updateVariantInputs();
    this.#updatePricing(model);
    this.#updateSummary();
    this.#updateImageGalleryForVariant();
    this.#updateAddToCartAvailability();
  }

  #ensureScopedItemSelection(model, itemState) {
    const currentState = { ...itemState };
    if (this.#findScopedVariant(model, currentState, true)) return currentState;

    const candidates = model.variants.filter((variant) => variant.available && this.#variantMatchesScopedGlobal(model, variant));
    const bestVariant = candidates.sort((a, b) => {
      const score = (variant) => model.scopedItemOptions.reduce((total, option) => (
        total + (String(variant.options?.[option.index]) === String(currentState[option.index] || '') ? 1 : 0)
      ), 0);
      return score(b) - score(a);
    })[0];

    return bestVariant ? this.#getScopedItemStateFromVariant(model, bestVariant) : currentState;
  }

  #findScopedVariant(model, itemState, availableOnly = false) {
    return model?.variants.find((variant) => {
      if (availableOnly && !variant.available) return false;
      if (!this.#variantMatchesScopedGlobal(model, variant)) return false;
      return model.scopedItemOptions.every((option) => (
        String(variant.options?.[option.index]) === String(itemState?.[option.index] || '')
      ));
    }) || null;
  }

  #variantMatchesScopedGlobal(model, variant) {
    if (!model.scopedGlobalOption) return true;
    return String(variant.options?.[model.scopedGlobalOption.index]) === String(this.scopedState?.globalValue || '');
  }

  #isScopedOptionValueAvailable(model, itemIndex, option, value) {
    const itemState = { ...(this.scopedState.items[itemIndex] || {}), [option.index]: value };
    return Boolean(this.#findScopedVariant(model, itemState, true));
  }

  #getScopedVariantIdsForQuantity(model, quantity) {
    if (!this.isScopedMode || !this.scopedState) return [];
    if (!model?.scopedConfigurationValid) return Array(quantity).fill(null);
    return this.scopedState.items.slice(0, quantity).map((itemState) => {
      const variant = this.#findScopedVariant(model, itemState, true);
      return variant ? String(variant.id) : null;
    });
  }

  #getScopedVariantIds() {
    const model = this.#getActiveModel();
    const quantity = this.#getSelectedOption() === 'trio' ? 3 : this.#getSelectedOption() === 'dual' ? 2 : 1;
    return this.#getScopedVariantIdsForQuantity(model, quantity);
  }

  #getScopedVariantDisplayTitle(model, variantId) {
    const variant = this.#findVariantById(model, variantId);
    if (!variant) return '';
    const configuredOptions = [model.scopedGlobalOption, ...model.scopedItemOptions].filter(Boolean);
    return configuredOptions
      .map((option) => String(variant.options?.[option.index] || '').trim())
      .filter(Boolean)
      .join(' / ');
  }

  #resolveSelections(model, resetSelections) {
    const fallbackId = model.defaultVariantId;
    const currentSelections = {
      main: this.container.querySelector('[data-type-radio="1"]:checked')?.value || fallbackId,
      variant2: this.#getVariantIdFromRadio(2) || fallbackId,
      variant3: this.#getVariantIdFromRadio(3) || fallbackId,
      variant4: this.#getVariantIdFromRadio(4) || fallbackId
    };

    if (resetSelections) {
      return {
        main: fallbackId,
        variant2: fallbackId,
        variant3: fallbackId,
        variant4: fallbackId
      };
    }

    const validIds = new Set(model.selectorVariants.map((variant) => variant.id));
    return {
      main: validIds.has(currentSelections.main) ? currentSelections.main : fallbackId,
      variant2: validIds.has(currentSelections.variant2) ? currentSelections.variant2 : fallbackId,
      variant3: validIds.has(currentSelections.variant3) ? currentSelections.variant3 : fallbackId,
      variant4: validIds.has(currentSelections.variant4) ? currentSelections.variant4 : fallbackId
    };
  }

  #renderTypeOptions(model, selectedId) {
    if (!this.typeOptionsContainer) return;

    const selectorVariantCount = model.selectorVariants.length || 1;
    this.typeOptionsContainer.innerHTML = model.selectorVariants.map((variant, index) => this.#buildTypeOptionMarkup(variant, selectedId, index === 0)).join('');
    this.#applyResponsiveTypeOptionsLayout(selectorVariantCount);
  }

  #applyResponsiveTypeOptionsLayout(selectorVariantCount) {
    if (!this.typeOptionsContainer) return;

    const isMobile = window.matchMedia('(max-width: 740px)').matches;
    const typeOptions = Array.from(this.typeOptionsContainer.querySelectorAll('.single-dual-bundle__type-option'));
    const desktopColumns = Math.max(Math.min(selectorVariantCount, 5), 1);
    const mobileColumns = Math.max(Math.min(selectorVariantCount, 4), 1);

    [
      'display',
      'gridTemplateColumns',
      'gridAutoFlow',
      'gridAutoColumns',
      'flexWrap',
      'gap',
      'width',
      'padding',
      'overflowX',
      'overflowY',
      'whiteSpace',
      'scrollSnapType',
      'scrollbarWidth',
      'webkitOverflowScrolling',
      'overscrollBehaviorX',
      'touchAction',
      'fontSize'
    ].forEach((prop) => {
      this.typeOptionsContainer.style[prop] = '';
    });

    typeOptions.forEach((typeOption) => {
      [
        'display',
        'flex',
        'width',
        'minWidth',
        'marginRight',
        'whiteSpace',
        'scrollSnapAlign',
        'scrollSnapStop'
      ].forEach((prop) => {
        typeOption.style[prop] = '';
      });
    });

    this.typeOptionsContainer.style.setProperty('--sd-type-columns', String(desktopColumns));
    this.typeOptionsContainer.style.setProperty('--sd-type-mobile-columns', String(mobileColumns));

    if (isMobile) {
      this.typeOptionsContainer.dataset.mobileLayout = selectorVariantCount <= 4 ? 'grid' : 'scroll';
      return;
    }

    this.typeOptionsContainer.dataset.mobileLayout = '';
  }

  #buildTypeOptionMarkup(variant, selectedId, isFirst) {
    const checked = variant.id === selectedId ? 'checked' : '';
    const escapedVariantId = this.#escapeHtml(variant.id);
    const radioId = `single-dual-variant-1-${this.instanceId}-${escapedVariantId}`;
    const radioName = `single-dual-variant-1-${this.instanceId}`;
    const descriptionMarkup = this.#buildVariantDescriptionMarkup(variant.description);
    const popularBadgeMarkup = isFirst && this.config?.popularBadge
      ? `<span class="single-dual-bundle__type-badge">${this.#escapeHtml(this.config.popularBadge)}</span>`
      : '';
    const imageMarkup = variant.imageUrl
      ? `<img src="${this.#escapeHtml(variant.imageUrl)}" alt="${this.#escapeHtml(variant.displayTitle)}" width="200" height="200" loading="lazy">`
      : '';

    return `
      <div class="single-dual-bundle__type-option">
        <input
          type="radio"
          id="${radioId}"
          name="${radioName}"
          value="${escapedVariantId}"
          data-variant-title="${this.#escapeHtml(variant.displayTitle)}"
          data-variant-raw-title="${this.#escapeHtml(variant.rawTitle)}"
          data-type-radio="1"
          data-is-first="${isFirst ? 'true' : 'false'}"
          ${checked}
          class="single-dual-bundle__type-radio"
        >
        <label for="${radioId}" class="single-dual-bundle__type-label" tabindex="0">
          ${popularBadgeMarkup}
          <div class="single-dual-bundle__type-image">
            <div class="single-dual-bundle__type-checkmark">
              <svg viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path>
              </svg>
            </div>
            ${imageMarkup}
          </div>
          <div class="single-dual-bundle__type-info">
            <div class="single-dual-bundle__type-name">${this.#escapeHtml(variant.displayTitle)}</div>
            ${descriptionMarkup}
          </div>
        </label>
      </div>
    `;
  }

  #buildVariantDescriptionMarkup(description) {
    if (!description) return '';

    const bullets = description
      .split(',')
      .map((bullet) => bullet.trim())
      .filter(Boolean)
      .map((bullet) => `<li>&bull; ${this.#escapeHtml(bullet)}</li>`)
      .join('');

    if (!bullets) return '';

    return `
      <div class="single-dual-bundle__type-description">
        <span class="single-dual-bundle__type-description-label">Empfohlen für:</span>
        <ul class="single-dual-bundle__type-description-list">${bullets}</ul>
      </div>
    `;
  }

  #renderVariantOptions(model, radioNumber, selectedId) {
    const container = this.variantOptionsContainers[radioNumber];
    if (!container) return;

    const labelNumber = radioNumber - 1;
    const label = this.container.querySelector(`[data-variant-group-label="${labelNumber}"]`);
    if (label) {
      const itemUnitLabel = String(this.config?.itemUnitLabel || 'Sattel').trim() || 'Sattel';
      label.textContent = `${itemUnitLabel} ${labelNumber} auswählen:`;
    }

    container.innerHTML = model.selectorVariants.map((variant) => {
      const checked = variant.id === selectedId ? 'checked' : '';
      const escapedVariantId = this.#escapeHtml(variant.id);
      const radioId = `sd-v${radioNumber}-${this.instanceId}-${escapedVariantId}`;
      const radioName = `single-dual-variant-${radioNumber}-${this.instanceId}`;
      return `
        <div class="single-dual-bundle__variant-option">
          <input
            type="radio"
            id="${radioId}"
            name="${radioName}"
            value="${escapedVariantId}"
            data-variant-radio="${radioNumber}"
            data-variant-title="${this.#escapeHtml(variant.displayTitle)}"
            data-variant-raw-title="${this.#escapeHtml(variant.rawTitle)}"
            ${checked}
            class="single-dual-bundle__variant-option-radio"
          >
          <label for="${radioId}" class="single-dual-bundle__variant-option-label" tabindex="0">
            <span class="single-dual-bundle__variant-option-dot"></span>
            <span class="single-dual-bundle__variant-option-name">${this.#escapeHtml(variant.displayTitle)}</span>
          </label>
        </div>
      `;
    }).join('');
  }

  #updatePricing(model) {
    if (this.isScopedMode) {
      this.#updateScopedPricing(model);
      return;
    }

    const singlePricingState = this.#updateSingleCardPricing(model);
    const duoPricingState = this.#resolveBundlePricing(model, singlePricingState, 2);
    const trioPricingState = this.#resolveBundlePricing(model, singlePricingState, 3);

    this.#applyBundlePricing('dual', duoPricingState);
    this.#applyBundlePricing('trio', trioPricingState);
    this.#publishPricingState(singlePricingState, duoPricingState, trioPricingState);
  }

  #updateScopedPricing(model) {
    const pricingModel = this.#getScopedPricingProfile(model);
    const singleVariantId = this.#getScopedVariantIdsForQuantity(model, 1)[0] || model.defaultVariantId;
    const singlePricingState = this.#resolveSingleCardPricing(pricingModel, singleVariantId);
    const duoPricingState = this.#resolveScopedBundlePricing(pricingModel, 2);
    const trioPricingState = this.#resolveScopedBundlePricing(pricingModel, 3);

    if (this.singlePriceElement) {
      this.singlePriceElement.textContent = this.#formatMoney(singlePricingState.priceCents);
      this.#updatePricingElementMetadata(this.singlePriceElement, singlePricingState);
    }
    if (this.singleComparePriceElement) {
      this.#updatePricingElementMetadata(this.singleComparePriceElement, singlePricingState);
      if (singlePricingState.comparePriceCents > 0) {
        this.singleComparePriceElement.textContent = this.#formatMoney(singlePricingState.comparePriceCents);
        this.singleComparePriceElement.style.display = '';
      } else {
        this.singleComparePriceElement.style.display = 'none';
      }
    }
    if (this.singleSavingsAmountElement) {
      this.singleSavingsAmountElement.textContent = this.#formatMoney(singlePricingState.savingsCents);
    }
    const singleBadge = this.savingsBadgeElements.single;
    const showSingleSavings = singlePricingState.usingIntelligems
      ? singlePricingState.savingsCents > 0 && singlePricingState.savingsPercent > 0
      : Number(pricingModel.singleDiscountBadge || 0) > 0;
    const singleSavingsPercent = singlePricingState.usingIntelligems
      ? singlePricingState.savingsPercent
      : Number(pricingModel.singleDiscountBadge || 0);
    if (this.singleSavingsPercentElement) {
      this.singleSavingsPercentElement.textContent = String(singleSavingsPercent);
    }
    if (singleBadge) {
      singleBadge.style.display = showSingleSavings ? '' : 'none';
    }
    this.#applyBundlePricing('dual', duoPricingState);
    this.#applyBundlePricing('trio', trioPricingState);
    this.#publishPricingState(
      {
        ...singlePricingState,
        totalPriceCents: singlePricingState.priceCents,
        totalComparePriceCents: singlePricingState.comparePriceCents
      },
      duoPricingState,
      trioPricingState
    );
    this.#syncIntelligemsRefreshState({
      variantId: singlePricingState.variantId,
      usingIntelligems: singlePricingState.usingIntelligems
    });
  }

  #getScopedPricingProfile(model) {
    if (!this.isScopedMode) return model;

    const secondaryGlobalValue = String(this.config?.scopedSecondaryGlobalValue || '').trim();
    const secondaryPricing = this.config?.scopedSecondaryPricing;
    const activeGlobalValue = String(this.scopedState?.globalValue || '').trim();
    const hasSecondaryPricing = Boolean(
      secondaryPricing
      && [
        'singlePrice',
        'singleComparePrice',
        'duoPrice',
        'duoComparePrice',
        'trioPrice',
        'trioComparePrice',
        'singleDiscountBadge',
        'duoDiscountBadge',
        'trioDiscountBadge',
        'testingDuoDiscount',
        'testingTrioDiscount'
      ].some((key) => Number(secondaryPricing[key] || 0) > 0)
    );

    if (!model?.scopedGlobalOption || !secondaryGlobalValue || !hasSecondaryPricing || activeGlobalValue !== secondaryGlobalValue) {
      return model;
    }

    return {
      ...model,
      singlePrice: Number(secondaryPricing.singlePrice || 0),
      singleComparePrice: Number(secondaryPricing.singleComparePrice || 0),
      duoPrice: Number(secondaryPricing.duoPrice || 0),
      duoComparePrice: Number(secondaryPricing.duoComparePrice || 0),
      trioPrice: Number(secondaryPricing.trioPrice || 0),
      trioComparePrice: Number(secondaryPricing.trioComparePrice || 0),
      testingDuoDiscount: Number(secondaryPricing.testingDuoDiscount || 0),
      testingTrioDiscount: Number(secondaryPricing.testingTrioDiscount || 0),
      singleDiscountBadge: Number(secondaryPricing.singleDiscountBadge || 0),
      duoDiscountBadge: Number(secondaryPricing.duoDiscountBadge || 0),
      trioDiscountBadge: Number(secondaryPricing.trioDiscountBadge || 0)
    };
  }

  #resolveScopedBundlePricing(model, quantity) {
    const bundleKey = quantity === 2 ? 'duo' : 'trio';
    const variantIds = this.#getScopedVariantIdsForQuantity(model, quantity);
    const prices = variantIds.map((variantId) => this.#getScopedVariantPricing(model, variantId));
    const discountCents = quantity === 2
      ? Number(model.testingDuoDiscount || 0)
      : quantity === 3 ? Number(model.testingTrioDiscount || 0) : 0;
    const manualTotalPriceCents = Number(model?.[`${bundleKey}Price`] || 0);
    const manualTotalComparePriceCents = Number(model?.[`${bundleKey}ComparePrice`] || 0);
    const manualSavingsCents = Math.max(manualTotalComparePriceCents - manualTotalPriceCents, 0);
    const manualSavingsPercent = Number(model?.[`${bundleKey}DiscountBadge`] || 0);
    const pricingState = {
      productId: String(model.productId || this.config?.currentProductId || ''),
      variantId: variantIds[0] || '',
      totalPriceCents: manualTotalPriceCents,
      totalComparePriceCents: manualTotalComparePriceCents,
      pricePerItemCents: quantity > 0 ? Math.round(manualTotalPriceCents / quantity) : 0,
      comparePricePerItemCents: quantity > 0 ? Math.round(manualTotalComparePriceCents / quantity) : 0,
      savingsCents: manualSavingsCents,
      savingsPercent: manualSavingsPercent,
      usingIntelligems: false,
      usingPriceTesting: false
    };

    const unresolved = variantIds.some((variantId) => !variantId);
    const canUseAutomaticPricing = Boolean(
      model?.enablePriceTestingMode
      && !unresolved
      && prices.every((price) => Number(price.priceCents || 0) > 0)
      && (prices.some((price) => price.usingIntelligems) || manualTotalPriceCents <= 0)
    );

    if (!canUseAutomaticPricing) {
      return pricingState;
    }

    const summedPriceCents = prices.reduce((sum, price) => sum + price.priceCents, 0);
    const summedComparePriceCents = prices.reduce((sum, price) => sum + price.comparePriceCents, 0);
    pricingState.totalPriceCents = Math.max(summedPriceCents - discountCents, 0);
    pricingState.totalComparePriceCents = summedComparePriceCents;
    pricingState.pricePerItemCents = quantity > 0 ? Math.round(pricingState.totalPriceCents / quantity) : 0;
    pricingState.comparePricePerItemCents = quantity > 0 ? Math.round(summedComparePriceCents / quantity) : 0;
    pricingState.savingsCents = Math.max(summedComparePriceCents - pricingState.totalPriceCents, 0);
    pricingState.savingsPercent = summedComparePriceCents > pricingState.totalPriceCents
      ? Math.round((pricingState.savingsCents * 100) / summedComparePriceCents)
      : 0;
    pricingState.usingIntelligems = prices.some((price) => price.usingIntelligems);
    pricingState.usingPriceTesting = pricingState.usingIntelligems;

    return pricingState;
  }

  #getScopedVariantPricing(model, variantId) {
    const variant = this.#findVariantById(model, variantId);
    const intelligemsPriceCents = this.#getIntelligemsPriceCents(variantId);
    const intelligemsComparePriceCents = this.#getIntelligemsComparePriceCents(variantId);
    return {
      priceCents: intelligemsPriceCents ?? this.#getVariantPriceCents(variant),
      comparePriceCents: intelligemsComparePriceCents ?? this.#getVariantComparePriceCents(variant),
      usingIntelligems: intelligemsPriceCents != null
    };
  }

  #updateSingleCardPricing(model) {
    if (!model) return;

    const variantId = this.container.querySelector('[data-type-radio="1"]:checked')?.value || model.defaultVariantId;
    const pricingState = this.#resolveSingleCardPricing(model, variantId);
    this.#syncIntelligemsRefreshState(pricingState);

    if (this.singlePriceElement) {
      this.singlePriceElement.textContent = this.#formatMoney(pricingState.priceCents);
      this.#updatePricingElementMetadata(this.singlePriceElement, pricingState);
    }

    if (this.singleComparePriceElement) {
      this.#updatePricingElementMetadata(this.singleComparePriceElement, pricingState);
      if (pricingState.comparePriceCents > 0) {
        this.singleComparePriceElement.textContent = this.#formatMoney(pricingState.comparePriceCents);
        this.singleComparePriceElement.style.display = '';
      } else {
        this.singleComparePriceElement.style.display = 'none';
      }
    }

    const singleBadge = this.savingsBadgeElements.single;
    const showSavings = pricingState.usingIntelligems
      ? pricingState.savingsCents > 0 && pricingState.savingsPercent > 0
      : Number(model.singleDiscountBadge) > 0;

    if (this.singleSavingsAmountElement) {
      this.singleSavingsAmountElement.textContent = this.#formatMoney(pricingState.savingsCents);
      this.#updatePricingElementMetadata(this.singleSavingsAmountElement, pricingState);
    }

    if (this.singleSavingsPercentElement) {
      const percentValue = pricingState.usingIntelligems ? pricingState.savingsPercent : Number(model.singleDiscountBadge || 0);
      this.singleSavingsPercentElement.textContent = String(percentValue);
      this.#updatePricingElementMetadata(this.singleSavingsPercentElement, pricingState);
    }

    if (singleBadge) {
      singleBadge.style.display = showSavings ? '' : 'none';
    }

    return pricingState;
  }

  #resolveSingleCardPricing(model, variantId) {
    const variant = this.#findVariantById(model, variantId);
    const pricingState = {
      productId: String(model.productId || this.config?.currentProductId || ''),
      variantId: String(variantId || model.defaultVariantId || ''),
      priceCents: Number(model.singlePrice || 0),
      comparePriceCents: Number(model.singleComparePrice || 0),
      savingsCents: Math.max(Number(model.singleComparePrice || 0) - Number(model.singlePrice || 0), 0),
      savingsPercent: Number(model.singleDiscountBadge || 0),
      usingIntelligems: false,
      usingVariantPriceFallback: false
    };

    const variantPriceCents = this.#getVariantPriceCents(variant);
    const variantComparePriceCents = this.#getVariantComparePriceCents(variant);

    if (pricingState.priceCents <= 0 && variantPriceCents > 0) {
      pricingState.priceCents = variantPriceCents;
      pricingState.comparePriceCents = variantComparePriceCents;
      pricingState.savingsCents = Math.max(pricingState.comparePriceCents - pricingState.priceCents, 0);
      pricingState.savingsPercent = pricingState.comparePriceCents > pricingState.priceCents
        ? Math.round((pricingState.savingsCents * 100) / pricingState.comparePriceCents)
        : 0;
      pricingState.usingVariantPriceFallback = true;
    }

    const intelligemsPriceCents = this.#getIntelligemsPriceCents(pricingState.variantId);
    if (intelligemsPriceCents == null) {
      return pricingState;
    }

    const intelligemsComparePriceCents = this.#getIntelligemsComparePriceCents(pricingState.variantId);
    pricingState.priceCents = intelligemsPriceCents;
    pricingState.comparePriceCents = intelligemsComparePriceCents ?? 0;
    pricingState.savingsCents = Math.max(pricingState.comparePriceCents - pricingState.priceCents, 0);
    pricingState.savingsPercent = pricingState.comparePriceCents > pricingState.priceCents
      ? Math.round((pricingState.savingsCents * 100) / pricingState.comparePriceCents)
      : 0;
    pricingState.usingIntelligems = true;
    pricingState.usingVariantPriceFallback = false;

    return pricingState;
  }

  #getVariantPriceCents(variant) {
    const priceCents = Number(variant?.price);
    return Number.isFinite(priceCents) && priceCents > 0 ? priceCents : 0;
  }

  #getVariantComparePriceCents(variant) {
    const comparePriceCents = Number(variant?.compare_at_price);
    return Number.isFinite(comparePriceCents) && comparePriceCents > 0 ? comparePriceCents : 0;
  }

  #getIntelligemsPriceCents(variantId) {
    const priceApi = window.igData?.price;
    if (!priceApi?.getPriceByVariantId || !variantId) return null;

    try {
      return this.#parsePriceStringToCents(priceApi.getPriceByVariantId(String(variantId)));
    } catch (error) {
      console.warn('Single/Dual Bundle: Failed to read IntelliGems price', error);
      return null;
    }
  }

  #getIntelligemsComparePriceCents(variantId) {
    const priceApi = window.igData?.price;
    if (!priceApi?.getComparePriceByVariantId || !variantId) return null;

    try {
      return this.#parsePriceStringToCents(priceApi.getComparePriceByVariantId(String(variantId)));
    } catch (error) {
      console.warn('Single/Dual Bundle: Failed to read IntelliGems compare price', error);
      return null;
    }
  }

  #hasIntelligemsPriceApi() {
    return Boolean(
      window.igData?.price?.getPriceByVariantId
      && window.igData?.price?.getComparePriceByVariantId
    );
  }

  #hasResolvedIntelligemsPricing(variantId, { expectsComparePrice = false } = {}) {
    const priceCents = this.#getIntelligemsPriceCents(variantId);
    if (!(Number.isFinite(Number(priceCents)) && Number(priceCents) > 0)) {
      return false;
    }

    if (!expectsComparePrice) {
      return true;
    }

    return this.#getIntelligemsComparePriceCents(variantId) != null;
  }

  #syncIntelligemsRefreshState(pricingState) {
    if (this.isIntelligemsRefreshTicking) return;

    const variantId = String(pricingState?.variantId || '');
    const expectsComparePrice = Number(this.#getActiveModel()?.singleComparePrice || 0) > 0;
    const hasResolvedPrice = pricingState?.usingIntelligems
      && this.#hasResolvedIntelligemsPricing(variantId, { expectsComparePrice });

    if (hasResolvedPrice) {
      this.#clearIntelligemsRefreshTimer();
      return;
    }

    if (variantId) {
      this.#scheduleIntelligemsRefresh(variantId);
    }
  }

  #refreshForIntelligemsReady() {
    this.#updatePricing(this.#getActiveModel());
    this.#updateSummary();
  }

  #clearIntelligemsRefreshTimer() {
    if (this.intelligemsRefreshTimer != null) {
      window.clearTimeout(this.intelligemsRefreshTimer);
      this.intelligemsRefreshTimer = null;
    }
  }

  #registerIntelligemsLifecycleListeners() {
    window.addEventListener('load', () => {
      this.#scheduleIntelligemsRefresh(null, { resetAttempts: true });
    });

    window.addEventListener('pageshow', () => {
      this.#scheduleIntelligemsRefresh(null, { resetAttempts: true });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.#scheduleIntelligemsRefresh(null, { resetAttempts: true });
      }
    });
  }

  #scheduleIntelligemsRefresh(variantId = null, { resetAttempts = false } = {}) {
    const activeVariantId = String(
      variantId
      || (this.isScopedMode ? this.#getDisplayVariantId() : null)
      || this.container.querySelector('[data-type-radio="1"]:checked')?.value
      || this.#getActiveModel()?.defaultVariantId
      || ''
    );

    if (!activeVariantId) return;

    if (resetAttempts || this.intelligemsRefreshVariantId !== activeVariantId) {
      this.intelligemsRefreshAttempts = 0;
      this.intelligemsRefreshVariantId = activeVariantId;
    }

    if (this.intelligemsRefreshTimer != null) {
      return;
    }

    const maxAttempts = 60;

    const tryRefresh = () => {
      this.intelligemsRefreshTimer = null;

      if (!document.body.contains(this.container)) {
        this.#clearIntelligemsRefreshTimer();
        return;
      }

      const nextVariantId = String(
        (this.isScopedMode ? this.#getDisplayVariantId() : null)
        || this.container.querySelector('[data-type-radio="1"]:checked')?.value
        || this.#getActiveModel()?.defaultVariantId
        || this.intelligemsRefreshVariantId
        || ''
      );

      if (!nextVariantId) {
        return;
      }

      if (this.intelligemsRefreshVariantId !== nextVariantId) {
        this.intelligemsRefreshVariantId = nextVariantId;
        this.intelligemsRefreshAttempts = 0;
      }

      const expectsComparePrice = Number(this.#getActiveModel()?.singleComparePrice || 0) > 0;

      if (this.#hasResolvedIntelligemsPricing(this.intelligemsRefreshVariantId, { expectsComparePrice })) {
        this.isIntelligemsRefreshTicking = true;
        try {
          this.#refreshForIntelligemsReady();
        } finally {
          this.isIntelligemsRefreshTicking = false;
        }
        this.#clearIntelligemsRefreshTimer();
        return;
      }

      this.intelligemsRefreshAttempts += 1;
      if (this.intelligemsRefreshAttempts >= maxAttempts) {
        this.#clearIntelligemsRefreshTimer();
        return;
      }

      this.isIntelligemsRefreshTicking = true;
      try {
        this.#refreshForIntelligemsReady();
      } finally {
        this.isIntelligemsRefreshTicking = false;
      }

      this.intelligemsRefreshTimer = window.setTimeout(tryRefresh, 150);
    };

    this.intelligemsRefreshTimer = window.setTimeout(tryRefresh, 0);
  }

  #parsePriceStringToCents(value) {
    if (value == null || value === '') return null;

    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.round(value * 100);
    }

    let normalizedValue = String(value).trim().replace(/[^\d,.-]/g, '');
    if (!normalizedValue) return null;

    const hasComma = normalizedValue.includes(',');
    const hasDot = normalizedValue.includes('.');

    if (hasComma && hasDot) {
      if (normalizedValue.lastIndexOf(',') > normalizedValue.lastIndexOf('.')) {
        normalizedValue = normalizedValue.replace(/\./g, '').replace(',', '.');
      } else {
        normalizedValue = normalizedValue.replace(/,/g, '');
      }
    } else if (hasComma) {
      normalizedValue = normalizedValue.replace(',', '.');
    }

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? Math.round(parsedValue * 100) : null;
  }

  #updatePricingElementMetadata(element, pricingState) {
    if (!element) return;

    if (pricingState.productId) {
      element.setAttribute('data-product-id', pricingState.productId);
    }

    if (pricingState.variantId) {
      element.setAttribute('data-variant-id', pricingState.variantId);
    }
  }

  #updateSavingsBadge(key, value) {
    const badgeElement = this.savingsBadgeElements[key];
    const valueElement = this.savingsValueElements[key];
    if (!badgeElement || !valueElement) return;

    if (Number(value) > 0) {
      badgeElement.style.display = '';
      valueElement.textContent = String(value);
    } else {
      badgeElement.style.display = 'none';
    }
  }

  #applyBundlePricing(key, pricingState) {
    const priceElement = key === 'dual' ? this.duoPriceElement : this.trioPriceElement;
    const compareElement = key === 'dual' ? this.duoComparePriceElement : this.trioComparePriceElement;

    if (priceElement) {
      priceElement.textContent = this.#formatMoney(pricingState.pricePerItemCents);
      this.#updatePricingElementMetadata(priceElement, pricingState);
    }

    if (compareElement) {
      this.#updatePricingElementMetadata(compareElement, pricingState);
      if (pricingState.comparePricePerItemCents > 0) {
        compareElement.textContent = this.#formatMoney(pricingState.comparePricePerItemCents);
        compareElement.style.display = '';
      } else {
        compareElement.style.display = 'none';
      }
    }

    this.#updateSavingsBadge(key, pricingState.savingsPercent);
  }

  #publishPricingState(singlePricingState, duoPricingState, trioPricingState) {
    if (!this.container) return;

    const pricingStates = {
      single: {
        productId: singlePricingState?.productId || '',
        variantId: singlePricingState?.variantId || '',
        totalPriceCents: Number(singlePricingState?.priceCents || 0),
        totalComparePriceCents: Number(singlePricingState?.comparePriceCents || 0),
        pricePerItemCents: Number(singlePricingState?.priceCents || 0),
        comparePricePerItemCents: Number(singlePricingState?.comparePriceCents || 0),
        savingsPercent: Number(singlePricingState?.savingsPercent || 0)
      },
      dual: {
        productId: duoPricingState?.productId || '',
        variantId: duoPricingState?.variantId || '',
        totalPriceCents: Number(duoPricingState?.totalPriceCents || 0),
        totalComparePriceCents: Number(duoPricingState?.totalComparePriceCents || 0),
        pricePerItemCents: Number(duoPricingState?.pricePerItemCents || 0),
        comparePricePerItemCents: Number(duoPricingState?.comparePricePerItemCents || 0),
        savingsPercent: Number(duoPricingState?.savingsPercent || 0)
      },
      trio: {
        productId: trioPricingState?.productId || '',
        variantId: trioPricingState?.variantId || '',
        totalPriceCents: Number(trioPricingState?.totalPriceCents || 0),
        totalComparePriceCents: Number(trioPricingState?.totalComparePriceCents || 0),
        pricePerItemCents: Number(trioPricingState?.pricePerItemCents || 0),
        comparePricePerItemCents: Number(trioPricingState?.comparePricePerItemCents || 0),
        savingsPercent: Number(trioPricingState?.savingsPercent || 0)
      }
    };

    Object.entries(pricingStates).forEach(([key, pricingState]) => {
      this.container.setAttribute(`data-pricing-${key}-product-id`, String(pricingState.productId || ''));
      this.container.setAttribute(`data-pricing-${key}-variant-id`, String(pricingState.variantId || ''));
      this.container.setAttribute(`data-pricing-${key}-total-price-cents`, String(pricingState.totalPriceCents));
      this.container.setAttribute(`data-pricing-${key}-total-compare-price-cents`, String(pricingState.totalComparePriceCents));
      this.container.setAttribute(`data-pricing-${key}-price-per-item-cents`, String(pricingState.pricePerItemCents));
      this.container.setAttribute(`data-pricing-${key}-compare-price-per-item-cents`, String(pricingState.comparePricePerItemCents));
      this.container.setAttribute(`data-pricing-${key}-savings-percent`, String(pricingState.savingsPercent));
    });

    this.container.dispatchEvent(new CustomEvent('single-dual-bundle:pricing-updated', {
      bubbles: true,
      detail: pricingStates
    }));
  }

  #resolveBundlePricing(model, singlePricingState, quantity) {
    const bundleKey = quantity === 2 ? 'duo' : 'trio';
    const totalPriceCents = Number(model?.[`${bundleKey}Price`] || 0);
    const totalComparePriceCents = Number(model?.[`${bundleKey}ComparePrice`] || 0);
    const fallbackDiscountBadge = Number(model?.[`${bundleKey}DiscountBadge`] || 0);
    const testingDiscountCents = Number(model?.[quantity === 2 ? 'testingDuoDiscount' : 'testingTrioDiscount'] || 0);
    const pricingState = {
      productId: String(singlePricingState?.productId || model?.productId || this.config?.currentProductId || ''),
      variantId: String(singlePricingState?.variantId || model?.defaultVariantId || ''),
      totalPriceCents,
      totalComparePriceCents,
      pricePerItemCents: Math.round(totalPriceCents / quantity),
      comparePricePerItemCents: Math.round(totalComparePriceCents / quantity),
      savingsPercent: fallbackDiscountBadge,
      usingPriceTesting: false
    };

    const canUseAutomaticPricing = Boolean(
      model?.enablePriceTestingMode
      && Number(singlePricingState?.priceCents || 0) > 0
      && (singlePricingState?.usingIntelligems || singlePricingState?.usingVariantPriceFallback)
    );

    if (!canUseAutomaticPricing) {
      return pricingState;
    }

    const liveBundleTotalCents = Math.max((Number(singlePricingState.priceCents || 0) * quantity) - testingDiscountCents, 0);
    const liveBundleCompareTotalCents = Math.max(Number(singlePricingState.comparePriceCents || 0) * quantity, 0);
    const savingsCents = Math.max(liveBundleCompareTotalCents - liveBundleTotalCents, 0);

    pricingState.totalPriceCents = liveBundleTotalCents;
    pricingState.totalComparePriceCents = liveBundleCompareTotalCents;
    pricingState.pricePerItemCents = Math.round(liveBundleTotalCents / quantity);
    pricingState.comparePricePerItemCents = Math.round(liveBundleCompareTotalCents / quantity);
    pricingState.savingsPercent = liveBundleCompareTotalCents > liveBundleTotalCents
      ? Math.round((savingsCents * 100) / liveBundleCompareTotalCents)
      : 0;
    pricingState.usingPriceTesting = Boolean(singlePricingState?.usingIntelligems);

    return pricingState;
  }

  #setModelRadioState() {
    this.modelRadios.forEach((radio) => {
      radio.checked = radio.value === this.selectedModelKey;
    });
  }

  #getActiveModel() {
    return this.models[this.selectedModelKey] || this.models.primary;
  }

  #getSelectedOption() {
    const selected = this.container.querySelector('[data-option-radio]:checked');
    return selected ? selected.value : 'single';
  }

  #handleOptionChange() {
    const option = this.#getSelectedOption();

    this.optionCards.forEach((card) => {
      const cardOption = card.getAttribute('data-option-card');
      card.classList.toggle('single-dual-bundle__option-card--selected', cardOption === option);
    });

    if (this.optionInput) {
      this.optionInput.value = option;
    }

    if (this.optionItemDual) {
      this.optionItemDual.classList.toggle('single-dual-bundle__option-item--expanded', option === 'dual');
    }
    if (this.optionItemTrio) {
      this.optionItemTrio.classList.toggle('single-dual-bundle__option-item--expanded', option === 'trio');
    }

    if (this.isScopedMode) {
      const targetSlot = option === 'trio'
        ? this.expandableSlotTrio
        : option === 'dual'
          ? this.expandableSlotDual
          : this.expandableSlotSingle;
      const didMove = this.bundleExpandable && targetSlot && this.bundleExpandable.parentElement !== targetSlot;

      if (didMove) {
        const prevSlot = this.bundleExpandable.parentElement;
        if (prevSlot?.matches('[data-bundle-expandable-slot]')) {
          prevSlot.classList.remove('single-dual-bundle__bundle-expandable-slot--expanded');
        }
        targetSlot.appendChild(this.bundleExpandable);
      }

      if (this.bundleExpandable) {
        this.bundleExpandable.classList.remove('single-dual-bundle__bundle-expandable--hidden');
      }

      [this.expandableSlotSingle, this.expandableSlotDual, this.expandableSlotTrio].forEach((slot) => {
        if (!slot || slot === targetSlot) return;
        slot.classList.remove('single-dual-bundle__bundle-expandable-slot--expanded');
      });

      if (targetSlot) {
        if (didMove) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              targetSlot.classList.add('single-dual-bundle__bundle-expandable-slot--expanded');
            });
          });
        } else {
          targetSlot.classList.add('single-dual-bundle__bundle-expandable-slot--expanded');
        }
      }

      this.#updateBundleNotification();
      this.#renderScopedSelection(this.#getActiveModel());
      this.#updateVariantInputs();
      this.#updatePricing(this.#getActiveModel());
      this.#updateSummary();
      this.#updateImageGalleryForVariant();
      this.#updateAddToCartAvailability();
      return;
    }

    if (option === 'single') {
      const currentSlot = this.bundleExpandable?.parentElement;
      if (currentSlot?.matches('[data-bundle-expandable-slot]')) {
        currentSlot.classList.remove('single-dual-bundle__bundle-expandable-slot--expanded');
        const expandable = this.bundleExpandable;
        const storage = this.expandableStorage;
        setTimeout(() => {
          if (expandable && storage && expandable.parentElement?.matches('[data-bundle-expandable-slot]')) {
            storage.appendChild(expandable);
            expandable.classList.add('single-dual-bundle__bundle-expandable--hidden');
          }
        }, 400);
      }
      this.#updateBundleGiftFooter();
      this.#updateVariantInputs();
      this.#updatePricing(this.#getActiveModel());
      this.#updateSummary();
      this.#updateImageGalleryForVariant();
      this.#updateAddToCartAvailability();
      return;
    }

    const targetSlot = option === 'trio' ? this.expandableSlotTrio : this.expandableSlotDual;
    const didMove = this.bundleExpandable && targetSlot && this.bundleExpandable.parentElement !== targetSlot;

    if (didMove) {
      const prevSlot = this.bundleExpandable.parentElement;
      if (prevSlot?.matches('[data-bundle-expandable-slot]')) {
        prevSlot.classList.remove('single-dual-bundle__bundle-expandable-slot--expanded');
      }
      targetSlot.appendChild(this.bundleExpandable);
    }

    if (this.bundleExpandable) {
      this.bundleExpandable.classList.remove('single-dual-bundle__bundle-expandable--hidden');
    }
    if (this.expandableSlotDual !== targetSlot && this.expandableSlotDual) {
      this.expandableSlotDual.classList.remove('single-dual-bundle__bundle-expandable-slot--expanded');
    }
    if (this.expandableSlotTrio !== targetSlot && this.expandableSlotTrio) {
      this.expandableSlotTrio.classList.remove('single-dual-bundle__bundle-expandable-slot--expanded');
    }

    if (targetSlot) {
      if (didMove) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            targetSlot.classList.add('single-dual-bundle__bundle-expandable-slot--expanded');
          });
        });
      } else {
        targetSlot.classList.add('single-dual-bundle__bundle-expandable-slot--expanded');
      }
    }

    if (this.bundleInfoText) {
      const rawText = option === 'trio'
        ? (this.config?.trioInfoText || 'Wähle drei Varianten für dein Trio-Set')
        : (this.config?.duoInfoText || 'Wähle zwei Varianten für dein Duo-Set');
      this.bundleInfoText.innerHTML = this.#formatGratisCopy(rawText);
    }

    this.#updateBundleGiftFooter();

    if (this.variantGroup3) {
      this.variantGroup3.style.display = option === 'trio' ? '' : 'none';
    }

    if (option === 'dual') {
      this.#syncVariantRadiosFromMain();
    }

    this.#updateVariantInputs();
    this.#updatePricing(this.#getActiveModel());
    this.#updateSummary();
    this.#updateImageGalleryForVariant();
  }

  #updateBundleNotification() {
    const option = this.#getSelectedOption();
    this.#updateBundleGiftFooter();

    if (!this.notificationBox) return;

    const shouldShow = option !== 'single';
    this.notificationBox.style.display = shouldShow ? '' : 'none';

    if (!shouldShow || !this.bundleInfoText) return;

    const rawText = option === 'trio'
      ? (this.config?.trioInfoText || 'Wähle drei Varianten für dein Trio-Set')
      : (this.config?.duoInfoText || 'Wähle zwei Varianten für dein Duo-Set');
    this.bundleInfoText.innerHTML = this.#formatGratisCopy(rawText);
  }

  #updateBundleGiftFooter() {
    if (!this.bundleGiftFooter || !this.bundleGiftFooterText) return;

    const option = this.#getSelectedOption();
    if (option === 'single') {
      this.bundleGiftFooter.hidden = true;
      this.bundleGiftFooterText.innerHTML = '';
      return;
    }

    const shouldShow = option === 'trio'
      ? Boolean(this.config?.showTrioGiftCopy && this.config?.trioGiftFooterText)
      : Boolean(this.config?.showDuoGiftCopy && this.config?.duoGiftFooterText);
    const rawText = option === 'trio'
      ? (this.config?.trioGiftFooterText || '')
      : (this.config?.duoGiftFooterText || '');

    this.bundleGiftFooter.hidden = !shouldShow;

    if (!shouldShow) {
      this.bundleGiftFooterText.innerHTML = '';
      return;
    }

    this.#renderGratisCopy(this.bundleGiftFooterText, rawText);
  }

  #syncFromMainVariant() {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      const selectedRadio = this.container.querySelector('[data-type-radio="1"]:checked');
      if (!selectedRadio) return;

      if (this.bundleVariant1Input) {
        this.bundleVariant1Input.value = selectedRadio.value;
      }

      if (this.#getSelectedOption() !== 'single') {
        this.#syncVariantRadiosFromMain();
      }

      this.#updateVariantInputs();
      this.#updatePricing(this.#getActiveModel());
      this.#updateSummary();
      this.#updateImageGalleryForVariant();
    } finally {
      setTimeout(() => {
        this.isUpdating = false;
      }, 10);
    }
  }

  #syncVariantRadiosFromMain() {
    const selectedRadio = this.container.querySelector('[data-type-radio="1"]:checked');
    if (!selectedRadio) return;

    const variantId = selectedRadio.value;
    [2, 3, 4].forEach((radioNumber) => {
      const match = this.container.querySelector(`[data-variant-radio="${radioNumber}"][value="${variantId}"]`);
      if (match) {
        match.checked = true;
      }
    });
  }

  #handleVariantRadioChange() {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      this.#syncMainFromVariantRadios();
      this.#updateVariantInputs();
      this.#updatePricing(this.#getActiveModel());
      this.#updateSummary();
      this.#updateImageGalleryForVariant();
    } finally {
      setTimeout(() => {
        this.isUpdating = false;
      }, 10);
    }
  }

  #syncMainFromVariantRadios() {
    const option = this.#getSelectedOption();
    if (option === 'single') return;

    const radio2 = this.container.querySelector('[data-variant-radio="2"]:checked');
    if (!radio2) return;

    const mainRadio = this.container.querySelector(`[data-type-radio="1"][value="${radio2.value}"]`);
    if (mainRadio && !mainRadio.checked) {
      mainRadio.checked = true;
    }
  }

  #getVariantIdFromRadio(radioNumber) {
    const radio = this.container.querySelector(`[data-variant-radio="${radioNumber}"]:checked`);
    return radio ? radio.value : null;
  }

  #getVariantTitleFromRadio(radioNumber) {
    const radio = this.container.querySelector(`[data-variant-radio="${radioNumber}"]:checked`);
    return radio ? radio.getAttribute('data-variant-title') : '';
  }

  #updateVariantInputs() {
    const option = this.#getSelectedOption();
    if (this.isScopedMode) {
      const variantIds = this.#getScopedVariantIds();
      if (this.bundleVariant1Input) this.bundleVariant1Input.value = variantIds[0] || '';
      if (this.bundleVariant2Input) this.bundleVariant2Input.value = variantIds[1] || variantIds[0] || '';
      if (this.bundleVariant3Input) this.bundleVariant3Input.value = variantIds[2] || variantIds[1] || variantIds[0] || '';
      return;
    }

    const mainVariantId = this.container.querySelector('[data-type-radio="1"]:checked')?.value || this.#getActiveModel().defaultVariantId;
    const variant2Id = this.#getVariantIdFromRadio(2) || mainVariantId;
    const variant3Id = this.#getVariantIdFromRadio(3) || mainVariantId;
    const variant4Id = this.#getVariantIdFromRadio(4) || mainVariantId;

    if (option === 'single') {
      if (this.bundleVariant1Input) this.bundleVariant1Input.value = mainVariantId;
      if (this.bundleVariant2Input) this.bundleVariant2Input.value = mainVariantId;
      if (this.bundleVariant3Input) this.bundleVariant3Input.value = mainVariantId;
      return;
    }

    if (this.bundleVariant1Input) this.bundleVariant1Input.value = variant2Id;
    if (this.bundleVariant2Input) this.bundleVariant2Input.value = variant3Id;
    if (this.bundleVariant3Input) this.bundleVariant3Input.value = option === 'trio' ? variant4Id : variant3Id;
  }

  #updateSummary() {
    const option = this.#getSelectedOption();

    if (this.isScopedMode) {
      const model = this.#getActiveModel();
      const selections = this.#getScopedVariantIds().map((variantId) => this.#getScopedVariantDisplayTitle(model, variantId)).filter(Boolean);
      const prefix = String(this.config?.scopedSummaryLabel || '').trim();
      const body = selections.map((selection) => `1x ${selection}`).join(' + ');
      const summary = prefix ? `${prefix} ${body}`.trim() : body;
      if (this.summaryText) this.summaryText.textContent = summary;
      return;
    }

    if (!this.summaryText) return;

    if (option === 'single') {
      const title = this.container.querySelector('[data-type-radio="1"]:checked')?.getAttribute('data-variant-title') || '';
      this.summaryText.textContent = `1x ${title}`;
      return;
    }

    if (option === 'dual') {
      const title2 = this.#getVariantTitleFromRadio(2);
      const title3 = this.#getVariantTitleFromRadio(3);
      this.summaryText.textContent = `1x ${title2} + 1x ${title3}`;
      return;
    }

    const title2 = this.#getVariantTitleFromRadio(2);
    const title3 = this.#getVariantTitleFromRadio(3);
    const title4 = this.#getVariantTitleFromRadio(4);
    this.summaryText.textContent = `1x ${title2} + 1x ${title3} + 1x ${title4}`;
  }

  #getDisplayVariantId() {
    if (this.isScopedMode) return this.#getScopedVariantIds()[0] || null;
    if (this.#getSelectedOption() === 'single') {
      return this.container.querySelector('[data-type-radio="1"]:checked')?.value || null;
    }
    return this.#getVariantIdFromRadio(2);
  }

  #updateImageGalleryForVariant() {
    const model = this.#getActiveModel();
    const variantId = this.#getDisplayVariantId();
    const variant = this.#findVariantById(model, variantId);
    if (!variant) return;

    const productForm = this.container.closest('.product-form');
    const form = this.container.closest('form')
      || productForm?.querySelector('form[is="product-form"]')
      || productForm?.querySelector('form');

    if (form && model.productId === this.config.currentProductId) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = variantId;
      }

      const variantForDispatch = { ...variant };
      if (!variantForDispatch.featured_media && (variant.featured_image || variant.image)) {
        variantForDispatch.featured_media = variant.featured_image || variant.image;
      }

      form.dispatchEvent(new CustomEvent('variant:changed', {
        bubbles: true,
        detail: { variant: variantForDispatch }
      }));
    }

    if (this.isScopedMode) this.#updateScopedUrlState(variantId);
    this.#scrollToVariantImage(variant);
  }

  #updateScopedUrlState(variantId) {
    if (!variantId || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    url.searchParams.set('bundle_option', this.#getSelectedOption());
    window.history.replaceState({}, '', url.toString());
  }

  #findVariantById(model, variantId) {
    if (!model || !variantId) return null;
    return model.variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  #scrollToVariantImage(variant) {
    if (!variant) return;

    let mediaId = null;
    if (variant.featured_media?.id) {
      mediaId = variant.featured_media.id;
    } else if (variant.image?.id) {
      mediaId = variant.image.id;
    } else if (variant.featured_image?.id) {
      mediaId = variant.featured_image.id;
    }

    if (!mediaId) return;

    const swiperEl = document.querySelector('[id*="-media-list"][data-transition-effect]');
    if (!swiperEl?.swiper) {
      setTimeout(() => this.#scrollToVariantImage(variant), 100);
      return;
    }

    const targetSlide = swiperEl.querySelector(`.swiper-slide[data-media-id="${mediaId}"]:not(.is-filtered)`);
    if (!targetSlide) return;

    const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide:not(.is-filtered)'));
    const slideIndex = slides.indexOf(targetSlide);
    if (slideIndex >= 0) {
      swiperEl.swiper.slideTo(slideIndex);
    }
  }

  #overrideAddToCart() {
    let productForm = this.container.closest('form');
    if (!productForm) {
      productForm = this.container.closest('.product-form')?.querySelector('form[is="product-form"]');
    }
    if (!productForm) {
      productForm = document.querySelector('form[action*="/cart/add"]');
    }
    if (!productForm) {
      console.error('Single/Dual Bundle: Product form not found');
      return;
    }

    productForm.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.#handleAddToCart();
      return false;
    }, true);
  }

  async #handleAddToCart() {
    if (this.isAddingToCart) {
      return;
    }
    this.isAddingToCart = true;

    const option = this.#getSelectedOption();
    const addToCartButton = this.container.closest('.product-form')?.querySelector('.product-form__add-button')
      || document.querySelector('.product-form__add-button');

    if (addToCartButton) {
      addToCartButton.disabled = true;
      addToCartButton.setAttribute('aria-busy', 'true');
    }

    try {
      const items = this.#buildAddToCartItems(option);
      if (!items.length) {
        throw new Error(option === 'single' ? 'No variant selected' : 'Please select all variants');
      }

      const cartData = await this.#addToCartMultiAPI(items);
      this.#refreshCart(cartData);
    } catch (error) {
      console.error('Single/Dual Bundle: Error adding to cart', error);
      this.#showErrorMessage(error.message);
    } finally {
      if (addToCartButton) {
        if (!this.isScopedMode) addToCartButton.disabled = false;
        addToCartButton.setAttribute('aria-busy', 'false');
      }
      this.isAddingToCart = false;
      this.#updateAddToCartAvailability();
    }
  }

  #buildAddToCartItems(option) {
    if (this.isScopedMode) return this.#buildScopedAddToCartItems();

    const properties = {
      _bundle: 'single-dual-bundle',
      _bundle_option: this.#getSelectedOption(),
      _bundle_model: this.selectedModelKey
    };

    if (option === 'single') {
      const variantId = this.container.querySelector('[data-type-radio="1"]:checked')?.value;
      return variantId ? [{ id: parseInt(variantId, 10), quantity: 1, properties }] : [];
    }

    if (option === 'dual') {
      const variant1 = this.#getVariantIdFromRadio(2);
      const variant2 = this.#getVariantIdFromRadio(3);
      if (!variant1 || !variant2) return [];

      if (variant1 === variant2) {
        return [{ id: parseInt(variant1, 10), quantity: 2, properties }];
      }

      return [
        { id: parseInt(variant1, 10), quantity: 1, properties },
        { id: parseInt(variant2, 10), quantity: 1, properties }
      ];
    }

    if (option === 'trio') {
      const variantIds = [this.#getVariantIdFromRadio(2), this.#getVariantIdFromRadio(3), this.#getVariantIdFromRadio(4)];
      if (variantIds.some((variantId) => !variantId)) return [];

      const counts = {};
      variantIds.forEach((variantId) => {
        counts[variantId] = (counts[variantId] || 0) + 1;
      });

      return Object.entries(counts).map(([variantId, quantity]) => ({
        id: parseInt(variantId, 10),
        quantity,
        properties
      }));
    }

    return [];
  }

  #buildScopedAddToCartItems() {
    const variantIds = this.#getScopedVariantIds();
    const expectedQuantity = this.#getSelectedOption() === 'trio' ? 3 : this.#getSelectedOption() === 'dual' ? 2 : 1;
    if (variantIds.length !== expectedQuantity || variantIds.some((variantId) => !variantId)) return [];

    const counts = {};
    variantIds.forEach((variantId) => {
      counts[variantId] = (counts[variantId] || 0) + 1;
    });

    return Object.entries(counts).map(([variantId, quantity]) => {
      const properties = {
        _bundle: 'single-dual-bundle',
        _bundle_option: this.#getSelectedOption(),
        _bundle_model: this.selectedModelKey
      };

      return { id: parseInt(variantId, 10), quantity, properties };
    });
  }

  #updateAddToCartAvailability() {
    if (!this.isScopedMode) return;
    const expectedQuantity = this.#getSelectedOption() === 'trio' ? 3 : this.#getSelectedOption() === 'dual' ? 2 : 1;
    const variantIds = this.#getScopedVariantIds();
    const isAvailable = variantIds.length === expectedQuantity && variantIds.every(Boolean);
    const addToCartButton = this.container.closest('.product-form')?.querySelector('.product-form__add-button')
      || document.querySelector('.product-form__add-button');
    if (addToCartButton && !this.isAddingToCart) {
      addToCartButton.disabled = !isAvailable;
      addToCartButton.setAttribute('aria-disabled', String(!isAvailable));
    }
  }

  async #addToCartMultiAPI(items) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items,
        sections: 'mini-cart'
      }),
      credentials: 'include'
    });

    const text = await response.text();

    if (!response.ok) {
      let errorMessage = 'Failed to add to cart';
      try {
        const error = JSON.parse(text);
        errorMessage = error.message || error.description || errorMessage;
      } catch (error) {
        if (response.status === 401 && !text) {
          errorMessage = 'Session expired or unauthorized. Please refresh the page and try again.';
        } else {
          errorMessage = text || errorMessage;
        }
      }
      throw new Error(errorMessage);
    }

    return JSON.parse(text);
  }

  async #refreshCart(cartDataFromAdd, options = {}) {
    const {
      openDrawer = true,
      showNotification = true
    } = typeof options === 'boolean'
      ? { openDrawer: options, showNotification: options }
      : options;

    try {
      let cartData;

      if (cartDataFromAdd && typeof cartDataFromAdd === 'object' && cartDataFromAdd.sections) {
        if (typeof cartDataFromAdd.item_count === 'number') {
          cartData = cartDataFromAdd;
        } else {
          const cartResponse = await fetch('/cart.js');
          cartData = await cartResponse.json();
          if (cartData && typeof cartData === 'object') {
            cartData.sections = cartDataFromAdd.sections;
          }
        }
      } else {
        const [cartResponse, sectionsResponse] = await Promise.all([
          fetch('/cart.js'),
          fetch('/?sections=mini-cart')
        ]);
        cartData = await cartResponse.json();
        const sectionsData = await sectionsResponse.json();
        if (cartData && typeof cartData === 'object') {
          cartData.sections = sectionsData;
        }
      }

      if (cartData && typeof cartData === 'object') {
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartData }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartData,
            openMiniCart: openDrawer && window.themeVariables?.settings?.cartType === 'drawer'
          }
        }));
      }

      if (showNotification) {
        this.#showSuccessNotification();
        setTimeout(() => this.#refreshCart(null, { openDrawer: false, showNotification: false }), 1500);
      }
    } catch (error) {
      console.error('Single/Dual Bundle: Error refreshing cart', error);
      const cartDrawer = document.querySelector('cart-drawer, [data-cart-drawer]');
      if (cartDrawer && window.themeVariables?.settings?.cartType === 'drawer') {
        cartDrawer.open = true;
      }
    }
  }

  #showSuccessNotification() {
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #2e9e7b;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
    `;
    notification.textContent = '✓ Zum Warenkorb hinzugefügt';
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.transition = 'opacity 0.3s';
      notification.style.opacity = '0';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  #showErrorMessage(message) {
    document.documentElement.dispatchEvent(new CustomEvent('product:add-error', {
      bubbles: true,
      detail: { message }
    }));
    alert(message);
  }

  #formatOptionDisplay(title, { useEbikeLabels = false } = {}) {
    const trimmedTitle = String(title || '').split(' - ')[0].trim();

    if (useEbikeLabels) {
      const ebikeLabels = {
        Trekking: 'Trekking-E-Bike',
        City: 'City-E-Bike',
        MTB: 'E-MTB',
        Mountainbike: 'E-MTB'
      };
      if (ebikeLabels[trimmedTitle]) return ebikeLabels[trimmedTitle];
    }

    if (trimmedTitle === 'Mountainbike') return 'MTB';
    return trimmedTitle;
  }

  #getVariantImageUrl(variant) {
    const potentialImage = variant.featured_media?.preview_image?.src
      || variant.featured_media?.src
      || variant.featured_image?.src
      || variant.image?.src
      || variant.image;

    return this.#normalizeUrl(potentialImage || '');
  }

  #normalizeUrl(url) {
    if (!url || typeof url !== 'string') return '';
    if (url.startsWith('//')) return `https:${url}`;
    return url;
  }

  #formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    return `${amount.toFixed(2).replace('.', ',')} €`;
  }

  #escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

const initSingleDualBundleSelectors = () => {
  document.querySelectorAll('[data-single-dual-bundle][data-single-dual-bundle-mode="standard"]').forEach((container) => {
    if (container.dataset.singleDualBundleInitialized === 'true') {
      return;
    }

    container.dataset.singleDualBundleInitialized = 'true';
    new SingleDualBundleSelector(container);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSingleDualBundleSelectors);
} else {
  initSingleDualBundleSelectors();
}
